import React from "react";
import { Trash2, PlusCircle } from "react-feather";
import { useListGuardServiceTypesQuery } from "../salesApi";

// Structurally copied from features/stock/components/LineItemsEditor.jsx
// (controlled rows/onChange, add/remove-row UX, key-stable rows) but with
// Sales' own columns - a different domain entirely, not a parametrization
// of the Stock one (no product/VAT/rate-from-master concepts here).
//
// `rate` is client-only (never sent to the API - CreateGuardRequirementLineRequest
// has no rate field at all, see SalesDtos.cs) - it only exists so the
// blended guardsRate/PDF line-item amounts can be computed the same way
// Flutter does (guard_requirement_line.dart's @JsonKey(includeToJson:false)).
export const emptyGuardRequirementRow = () => ({
  key: `greq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  serviceTypeId: "",
  deploymentType: "PERMANENT",
  shift: "DAY",
  schedule: "DAILY",
  startDate: new Date().toISOString().slice(0, 10),
  endDate: "",
  qty: "1",
  rate: "",
  remarks: "",
});

// sum(qty) - server derives GuardsCount from this, never accepted directly.
export const computeGuardsCount = (rows) => rows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0);

// The earliest Start Date across the lines that actually carry guards - this
// is the order's Requested Deployment Date, so it is never typed separately.
// ISO yyyy-mm-dd strings sort chronologically, so a plain string compare is
// enough. "" when no line has both a qty and a date.
export const computeEarliestStartDate = (rows) =>
  rows
    .filter((r) => (Number(r.qty) || 0) > 0 && r.startDate)
    .map((r) => r.startDate)
    .sort()[0] || "";

// Weighted average (sum(qty*rate)/sum(qty)) so guardsCount x guardsRate
// server-side reproduces the true per-line subtotal exactly - matches
// Flutter's own _blendedGuardsRate formula (enquiry_detail_screen.dart /
// create_enquiry_screen.dart) exactly.
export const computeBlendedGuardsRate = (rows) => {
  const totalQty = computeGuardsCount(rows);
  if (!totalQty) return 0;
  const totalAmount = rows.reduce((sum, r) => sum + (Number(r.qty) || 0) * (Number(r.rate) || 0), 0);
  return totalAmount / totalQty;
};

// Maps a row to the exact CreateGuardRequirementLineRequest wire shape
// (serviceTypeId, deploymentType, shift, schedule, startDate, endDate, qty,
// remarks) - rate/key are intentionally dropped.
export const toGuardRequirementRequests = (rows) =>
  rows.map((r) => ({
    serviceTypeId: Number(r.serviceTypeId),
    deploymentType: r.deploymentType,
    shift: r.shift,
    schedule: r.schedule,
    startDate: r.startDate,
    endDate: r.deploymentType === "TEMPORARY" ? r.endDate || null : null,
    qty: Number(r.qty) || 0,
    remarks: r.remarks || null,
  }));

const GuardRequirementsEditor = ({ rows, onChange, disabled = false, showRate = true }) => {
  const { data: serviceTypes = [] } = useListGuardServiceTypesQuery({ isActive: true });

  const updateRow = (key, patch) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => onChange([...rows, emptyGuardRequirementRow()]);
  const removeRow = (key) => onChange(rows.filter((r) => r.key !== key));

  return (
    <div className="table-responsive">
      <table className="table table-bordered align-middle">
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th style={{ minWidth: 160 }}>Service</th>
            <th style={{ width: 120 }}>Deployment</th>
            <th style={{ width: 90 }}>Shift</th>
            <th style={{ width: 130 }}>Schedule</th>
            <th style={{ width: 130 }}>Start Date</th>
            <th style={{ width: 130 }}>End Date</th>
            <th style={{ width: 80 }}>Qty</th>
            {showRate && <th style={{ width: 110 }}>Rate</th>}
            <th style={{ minWidth: 130 }}>Remarks</th>
            {!disabled && <th style={{ width: 50 }} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.key}>
              <td>{idx + 1}</td>
              <td>
                <select
                  className="form-control"
                  value={row.serviceTypeId}
                  onChange={(e) => updateRow(row.key, { serviceTypeId: e.target.value })}
                  disabled={disabled}
                >
                  <option value="">--Select--</option>
                  {serviceTypes.map((s) => (
                    <option key={s.serviceTypeId} value={s.serviceTypeId}>{s.serviceTypeName}</option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  className="form-control"
                  value={row.deploymentType}
                  onChange={(e) =>
                    updateRow(row.key, {
                      deploymentType: e.target.value,
                      endDate: e.target.value === "PERMANENT" ? "" : row.endDate,
                    })
                  }
                  disabled={disabled}
                >
                  <option value="PERMANENT">Permanent</option>
                  <option value="TEMPORARY">Temporary</option>
                </select>
              </td>
              <td>
                <select
                  className="form-control"
                  value={row.shift}
                  onChange={(e) => updateRow(row.key, { shift: e.target.value })}
                  disabled={disabled}
                >
                  <option value="DAY">Day</option>
                  <option value="NIGHT">Night</option>
                </select>
              </td>
              <td>
                <select
                  className="form-control"
                  value={row.schedule}
                  onChange={(e) => updateRow(row.key, { schedule: e.target.value })}
                  disabled={disabled}
                >
                  <option value="DAILY">Daily</option>
                  <option value="WEEKENDS">Weekends</option>
                  <option value="CUSTOM_DATES">Custom Dates</option>
                </select>
              </td>
              <td>
                <input
                  type="date"
                  className="form-control"
                  value={row.startDate}
                  onChange={(e) => updateRow(row.key, { startDate: e.target.value })}
                  disabled={disabled}
                />
              </td>
              <td>
                <input
                  type="date"
                  className="form-control"
                  value={row.endDate}
                  onChange={(e) => updateRow(row.key, { endDate: e.target.value })}
                  disabled={disabled || row.deploymentType !== "TEMPORARY"}
                  title={row.deploymentType !== "TEMPORARY" ? "Only applies to a Temporary deployment" : undefined}
                />
              </td>
              <td>
                <input
                  type="number"
                  className="form-control"
                  value={row.qty}
                  onChange={(e) => updateRow(row.key, { qty: e.target.value })}
                  disabled={disabled}
                />
              </td>
              {showRate && (
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={row.rate}
                    onChange={(e) => updateRow(row.key, { rate: e.target.value })}
                    disabled={disabled}
                  />
                </td>
              )}
              <td>
                <input
                  type="text"
                  className="form-control"
                  value={row.remarks || ""}
                  onChange={(e) => updateRow(row.key, { remarks: e.target.value })}
                  disabled={disabled}
                />
              </td>
              {!disabled && (
                <td>
                  <button type="button" className="btn btn-link text-danger p-1" onClick={() => removeRow(row.key)} title="Remove line">
                    <Trash2 size={16} />
                  </button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={11} className="text-center text-muted py-3">No guard requirement lines added yet.</td>
            </tr>
          )}
        </tbody>
      </table>
      {!disabled && (
        <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" onClick={addRow}>
          <PlusCircle size={14} /> Add Line
        </button>
      )}
    </div>
  );
};

export default GuardRequirementsEditor;
